import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ProductionOrdersList from './ProductionOrdersList';
import ViewProductionOrder from './ViewProductionOrder';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './ProductionOrders.css';

function ProductionOrders({ initialViewMode, openOrder, onOpenOrderConsumed, salesOrderData, stockReplenishmentData }) {
  const { t } = useTranslation('production');
  const [selectedOrderId, setSelectedOrderId] = useState(openOrder?.id || null);
  const [viewMode, setViewMode] = useState(initialViewMode || 'list'); // 'list', 'view', 'edit', 'create'
  // Inner tab of the opened order (e.g. 'tasks' when coming back from a task)
  const [orderInnerTab, setOrderInnerTab] = useState(openOrder?.innerTab || null);
  const [createFromSalesOrder, setCreateFromSalesOrder] = useState(salesOrderData || null);
  const [createForStockReplenishment, setCreateForStockReplenishment] = useState(stockReplenishmentData || null);

  // Update view mode when initialViewMode changes. Going back to 'list' (ProductionPage drops
  // openOrder once it has been opened) must not close the order that was just opened.
  useEffect(() => {
    if (initialViewMode && initialViewMode !== 'list') {
      setViewMode(initialViewMode);
    }
  }, [initialViewMode]);

  // Open the requested order (deep link / cross-module navigation). Keyed on the nonce so the same
  // order opens again on every navigation, then hand it back so a remount doesn't reopen it.
  useEffect(() => {
    if (!openOrder?.id) return;
    setSelectedOrderId(openOrder.id);
    setOrderInnerTab(openOrder.innerTab || null);
    setViewMode('view');
    if (onOpenOrderConsumed) onOpenOrderConsumed();
  }, [openOrder?.nonce]);

  // Handle sales order data
  useEffect(() => {
    if (salesOrderData) {
      setCreateFromSalesOrder(salesOrderData);
      // Also ensure we're in create mode when we have sales order data
      if (initialViewMode === 'create') {
        setViewMode('create');
      }
    }
  }, [salesOrderData, initialViewMode]);

  // Handle stock replenishment data
  useEffect(() => {
    if (stockReplenishmentData) {
      setCreateForStockReplenishment(stockReplenishmentData);
      // Also ensure we're in create mode when we have stock replenishment data
      if (initialViewMode === 'create') {
        setViewMode('create');
      }
    }
  }, [stockReplenishmentData, initialViewMode]);

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: t('toolbar.titles.productionOrders'),
          buttons: [
            {
              label: t('toolbar.buttons.newOrder'),
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: t('toolbar.buttons.refresh'),
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: t('toolbar.titles.viewProductionOrder'),
          showBack: true,
          onBack: handleBack,
          buttons: [
            {
              label: t('toolbar.buttons.edit'),
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.delete'),
              icon: '🗑️',
              onClick: handleDelete,
              variant: 'danger'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: t('toolbar.titles.editProductionOrder'),
          showBack: false,
          buttons: [
            {
              label: t('toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.save'),
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: t('toolbar.titles.newProductionOrder'),
          showBack: false,
          buttons: [
            {
              label: t('toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.create'),
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode]);

  const handleSelectOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setOrderInnerTab(null);
    setViewMode('view');
  };

  const handleNew = () => {
    setSelectedOrderId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewProductionOrder component
    if (window.viewProductionOrderRef && window.viewProductionOrderRef.handleDelete) {
      window.viewProductionOrderRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewProductionOrder component
    if (window.viewProductionOrderRef && window.viewProductionOrderRef.handleSave) {
      window.viewProductionOrderRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedOrderId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedOrderId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in ProductionOrdersList
    if (window.productionOrdersListRef && window.productionOrdersListRef.fetchOrders) {
      window.productionOrdersListRef.fetchOrders();
    }
  };

  const handleOrderSaved = (order) => {
    setSelectedOrderId(order.job_number || order.order_number || order.order_id || order.id);
    setViewMode('view');
    // Refresh the list
    if (window.productionOrdersListRef && window.productionOrdersListRef.fetchOrders) {
      window.productionOrdersListRef.fetchOrders();
    }
  };

  const handleOrderCreated = (orderId) => {
    setSelectedOrderId(orderId);
    setViewMode('view');
    // Refresh the list
    if (window.productionOrdersListRef && window.productionOrdersListRef.fetchOrders) {
      window.productionOrdersListRef.fetchOrders();
    }
  };

  return (
    <div className="production-orders-page">
      <ResizablePanels
        leftPanel={
          <ProductionOrdersList
            ref={ref => window.productionOrdersListRef = ref}
            onSelectOrder={handleSelectOrder}
            selectedOrderId={selectedOrderId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewProductionOrder
              ref={ref => window.viewProductionOrderRef = ref}
              orderId={selectedOrderId}
              onBack={handleBack}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              initialTab={orderInnerTab}
              salesOrderData={createFromSalesOrder}
              stockReplenishmentData={createForStockReplenishment}
              onSave={handleOrderSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
              onOrderCreated={handleOrderCreated}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default ProductionOrders;